import { useMemo } from "react";
import { LuSquareSplitHorizontal } from "react-icons/lu";
import styled from "styled-components";

import UnitSelection from "./UnitSelection";
import useSuspenseParameter from "../../../../../../clients/taxonomy/useParameter";
import * as UI from "../../../../../../modules/ui-kit";

const Styled = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 16px;

    & > * {
        flex: auto;
    }

    button {
        flex: none;
    }
`;

const RangeIcon = styled(LuSquareSplitHorizontal)`
    font-size: 1.8em;
`;

export default function ValueInput(props) {
    const { disabled, onChange, parameterID, properties } = props;

    const parameterQuery = useSuspenseParameter(parameterID);
    const { parameter } = parameterQuery.data;

    const Value = useMemo(
        () => {
            if (parameter?.values?.length) return EnumeratedValue;
            if (parameter?.dataType === "boolean") return BooleanValue;
            if (parameter?.dataType === "number") return NumberValue;
            if (parameter?.dataType === "text") return UI.TextInput;
            return () => {};
        },
        [parameter],
    );

    const [property] = properties;

    return (
        <Styled>
            <Value
                disabled={ disabled }
                onChange={ (value) => onChange(property, { value }) }
                parameter={ parameter }
                value={ property.value }
                unit={ property.unit }
            />
            <UnitSelection
                disabled={ disabled }
                onChange={ (unit) => onChange(property, { unit }) }
                parameter={ parameter }
                unit={ property.unit }
            />
            <UI.Button
                $appearance="subtle"
                disabled={ disabled }
                onClick={ () => onChange(property, undefined) }
            >
                [x]
            </UI.Button>
        </Styled>
    );
}

function BooleanValue(props) {
    return (
        <UI.SegmentedControl
            { ...props }
            options={
                [
                    { label: "yes", value: true },
                    { label: "no", value: false },
                ]
            }
        />
    );
}

function EnumeratedValue(props) {
    const { disabled, onChange, parameter } = props;
    return (
        <UI.Select
            { ...props }
            isDisabled={ disabled }
            onSelect={ onChange }
            options={
                parameter
                    .values
                    .map(
                        (value) => (
                            {
                                label: value.label[0].text,
                                value: value.id,
                            }
                        )
                    )
            }
        />
    );
}

function NumberValue(props) {
    const { onChange, value } = props;

    const isRange = typeof value === "object" && ("min" in value || "max" in value);
    const Component = isRange ? RangeValue : UI.NumberInput;

    return (
        <>
            <UI.Button
                $appearance={ isRange ? "primary" : "subtle" }
                $size="small"
                onClick={ () => onChange(isRange ? value.min ?? value.max : { min: value, max: value }) }
            >
                <RangeIcon />
            </UI.Button>
            <Component { ...props } />
        </>
    );
}

function RangeValue(props) {
    const { disabled, onChange, value } = props;
    return (
        <div style={ { alignItems: "center", display: "flex", flexDirection: "row", gap: "8px" } }>
            <UI.NumberInput
                disabled={ disabled }
                onChange={ (min) => onChange({ ...value, min }) }
                placeholder="min"
                value={ value.min }
            />
            -
            <UI.NumberInput
                disabled={ disabled }
                onChange={ (max) => onChange({ ...value, max }) }
                placeholder="max"
                value={ value.max }
            />
        </div>
    );
}
